'use strict';

import { Company } from '../Company';
import { User } from '../User/User';
import { Entity } from '../Entity';
import { Plan } from '../Plan';
import { IndPolicyGroup } from './IndPolicyGroup';
import { IndPolicyGroupCollection } from '../../Collections/Ind/IndPolicyGroupCollection';
import { IndPolicyAccVal } from './IndPolicyAccVal';
import { IndPolicyAccValCollection } from '../../Collections/Ind/IndPolicyAccValCollection';

/**
 * Types
 */
export const INDIVIDUAL_POLICY_STATUS_TYPE = {
  ACTIVE: 'Active',
  PENDING: 'Pending',
  REPLACED: 'Replaced',
  CANCELLED: 'Canceled',
  DECLINED: 'Declined',
} as const;

export type INDIVIDUAL_POLICY_STATUS_TYPE =
  (typeof INDIVIDUAL_POLICY_STATUS_TYPE)[keyof typeof INDIVIDUAL_POLICY_STATUS_TYPE];

export const INDIVIDUAL_POLICY_WRITTEN_AS = {
  NEW_TO_MEDICARE: 'New to Medicare',
  LIKE_PLAN_CHANGE: 'Like Plan Change',
  LIKE_PLAN_CHANGE_SAME_COMPANY: 'Like Plan Change (same company)',
  UNLIKE_PLAN_CHANGE: 'Unlike Plan Change',
  AOR_CHANGE: 'AOR Change',
  NA: 'NA',
} as const;

export type INDIVIDUAL_POLICY_WRITTEN_AS =
  (typeof INDIVIDUAL_POLICY_WRITTEN_AS)[keyof typeof INDIVIDUAL_POLICY_WRITTEN_AS];

export class IndPolicy extends Entity {
  protected policyId;
  protected policyCompany;
  protected policyPlan;
  protected policyType;
  protected policyEffective;
  protected policyAppDate;
  protected policyTermDate;
  protected policyStatus: INDIVIDUAL_POLICY_STATUS_TYPE;
  protected policyInitialComp;
  protected policyReplacing;
  protected policyNotes;
  protected policyEnrolled;
  protected policyWasAgent;
  protected policyHospNtwk;
  protected policyPcpNtwk;
  protected policyNumber;
  protected policyAppId;
  protected policyWrittenAs: INDIVIDUAL_POLICY_WRITTEN_AS;
  protected policySubType;
  protected policyPremium;
  protected policyPremiumTotal;
  protected policyComPrem;
  protected policyDb;
  protected policyTerm;
  protected policyMaturityDate;
  protected policyRiders;
  protected policyLives;
  protected policyDeductible;
  protected policySubsidy;
  protected policyOop;
  protected policyInitPay;
  protected policyAor;
  protected policyFronter;
  protected policySurvey;
  protected policyBenefitReminder;
  protected policySpif;
  protected policyUsingSubsidy;
  protected policyExchangeStatus;
  protected policyDraftDay;
  protected policyTpv;
  protected policyReferral;

  // computed
  protected recentAccValueIncrease;

  protected company: Company;
  protected plan: Plan;
  protected groupInsureds;
  protected policyAccountValues;
  protected enrollmentSource;

  constructor() {
    super();
  }

  public hydrate(data: any) {
    this.policyId = data.policyId;
    this.policyType = data.policyType;
    this.policyStatus = data.policyStatus;
    this.policyInitialComp = data.policyInitialComp;
    this.policyReplacing = data.policyReplacing;
    this.policyNotes = data.policyNotes;
    this.policyEnrolled = data.policyEnrolled;
    this.policyWasAgent = data.policyWasAgent;
    this.policyHospNtwk = data.policyHospNtwk;
    this.policyPcpNtwk = data.policyPcpNtwk;
    this.policyNumber = data.policyNumber;
    this.policyWrittenAs = data.policyWrittenAs;
    this.policySubType = data.policySubType;
    this.policyPremium = data.policyPremium;
    this.policyPremiumTotal = data.policyPremiumTotal;
    this.policyComPrem = data.policyComPrem;
    this.policyDb = data.policyDb;
    this.policyTerm = data.policyTerm;
    this.policyRiders = data.policyRiders;
    this.policyLives = data.policyLives;
    this.policyDeductible = data.policyDeductible;
    this.policyInitPay = data.policyInitPay;
    this.policyAor = data.policyAor;
    this.policyFronter = data.policyFronter;
    this.policySurvey = data.policySurvey;
    this.policyBenefitReminder = data.policyBenefitReminder;
    this.policySpif = data.policySpif;
    this.policyAppId = data.policyAppId;
    this.policySubsidy = data.policySubsidy;
    this.policyOop = data.policyOop;
    this.policyUsingSubsidy = data.policyUsingSubsidy;
    this.policyExchangeStatus = data.policyExchangeStatus;
    this.recentAccValueIncrease = data.recentAccValueIncrease;
    this.policyDraftDay = data.policyDraftDay;
    this.enrollmentSource = data.enrollmentSource;
    this.policyTpv = data.policyTpv;
    this.policyReferral = data.policyReferral;

    if (data.policyEffective) {
      const policyEffective = data.policyEffective.date.split(' ')[0].split('-');
      this.policyEffective =
        policyEffective[1] + '/' + policyEffective[2] + '/' + policyEffective[0];
    }

    if (data.policyTermDate) {
      const policyTermDate = data.policyTermDate.date.split(' ')[0].split('-');
      this.policyTermDate = policyTermDate[1] + '/' + policyTermDate[2] + '/' + policyTermDate[0];
    }

    if (data.policyAppDate) {
      const policyAppDate = data.policyAppDate.date.split(' ')[0].split('-');
      this.policyAppDate = policyAppDate[1] + '/' + policyAppDate[2] + '/' + policyAppDate[0];
    }

    if (data.policyMaturityDate) {
      const policyMaturityDate = data.policyMaturityDate.date.split(' ')[0].split('-');
      this.policyMaturityDate =
        policyMaturityDate[1] + '/' + policyMaturityDate[2] + '/' + policyMaturityDate[0];
    }

    if (data.company) {
      const company = new Company();

      company.hydrate(data.company);

      this.company = company;
    }

    if (data.plan) {
      const plan = new Plan();

      plan.hydrate(data.plan);

      this.plan = plan;
    }

    if (data.policyAor) {
      const policyAor = new User();

      policyAor.hydrate(data.policyAor);

      this.policyAor = policyAor;
    }

    if (data.groupInsureds) {
      const groupInsureds = new IndPolicyGroupCollection();

      data.groupInsureds.forEach((singleGroupInsureds) => {
        const groupInsured = new IndPolicyGroup();
        groupInsured.hydrate(singleGroupInsureds);

        groupInsureds.addItem(groupInsured);
      });

      this.groupInsureds = groupInsureds;
    }

    if (data.accValues) {
      const accValues = new IndPolicyAccValCollection();

      data.accValues.forEach((singleAccVal) => {
        const accVal = new IndPolicyAccVal();
        accVal.hydrate(singleAccVal);

        accValues.addItem(accVal);
      });

      this.policyAccountValues = accValues;
    }
  }

  public getPolicyId() {
    return this.policyId;
  }

  public getPolicyCompany() {
    return this.policyCompany;
  }

  public getPolicyPlan() {
    return this.policyPlan;
  }

  public getPolicyType() {
    return this.policyType;
  }

  public getPolicyEffective() {
    return this.policyEffective;
  }

  public getPolicyTermDate() {
    return this.policyTermDate;
  }

  public getPolicyAppDate() {
    return this.policyAppDate;
  }

  public getPolicyStatus() {
    return this.policyStatus;
  }

  public getPolicyInitialComp() {
    return this.policyInitialComp;
  }

  public getPolicyReplacing() {
    return this.policyReplacing;
  }

  public getPolicyNotes() {
    return this.policyNotes;
  }

  public getPolicyEnrolled() {
    return this.policyEnrolled;
  }

  public getPolicyWasAgent() {
    return this.policyWasAgent;
  }

  public getPolicyHospNtwk() {
    return this.policyHospNtwk;
  }

  public getPolicyPcpNtwk() {
    return this.policyPcpNtwk;
  }

  public getPolicyNumber() {
    return this.policyNumber;
  }

  public getPolicyAppId() {
    return this.policyAppId;
  }

  public getPolicyWrittenAs() {
    return this.policyWrittenAs;
  }

  public getPolicySubType() {
    return this.policySubType;
  }

  public getPolicyPremium() {
    return this.policyPremium;
  }

  public getPolicyPremiumTotal() {
    return this.policyPremiumTotal;
  }

  public getPolicyComPrem() {
    return this.policyComPrem;
  }

  public getPolicyDb() {
    return this.policyDb;
  }

  public getPolicyTerm() {
    return this.policyTerm;
  }

  public getPolicyMaturityDate() {
    return this.policyMaturityDate;
  }

  public getPolicyRiders() {
    return this.policyRiders;
  }

  public getPolicyLives() {
    return this.policyLives;
  }

  public getPolicyDeductible() {
    return this.policyDeductible;
  }

  public getPolicySubsidy() {
    return this.policySubsidy;
  }

  public getPolicyOop() {
    return this.policyOop;
  }

  public getPolicyUsingSubsidy() {
    return this.policyUsingSubsidy;
  }

  public getPolicyExchangeStatus() {
    return this.policyExchangeStatus;
  }

  public getPolicyInitPay() {
    return this.policyInitPay;
  }

  public getPolicyAor() {
    return this.policyAor;
  }

  public getPolicyFronter() {
    return this.policyFronter;
  }

  public getPolicySurvey() {
    return this.policySurvey;
  }

  public getPolicySpif() {
    return this.policySpif;
  }

  public getCompany() {
    return this.company;
  }

  public getPlan() {
    return this.plan;
  }

  public getGroupInsureds() {
    return this.groupInsureds;
  }

  public getPolicyBenefitReminder() {
    return this.policyBenefitReminder;
  }

  public getPolicyAccountValues() {
    return this.policyAccountValues;
  }

  public getPolicyAccountValuesLatestIncrease() {
    let increase = null;

    if (this.policyAccountValues.getItems().length > 1) {
      const items = this.policyAccountValues.getItems();
      const newest = items.at(-1).getAccumulationValue();
      const secondNewest = items.at(-2).getAccumulationValue();
      increase = ((newest - secondNewest) / secondNewest) * 100;
      increase = increase.toFixed(2);
    }

    return increase;
  }

  public getRecentAccValueIncrease() {
    return this.recentAccValueIncrease;
  }

  public getPolicyDraftDay() {
    return this.policyDraftDay;
  }

  public getEnrollmentSource() {
    return this.enrollmentSource;
  }

  public getPolicyTpv() {
    return this.policyTpv;
  }

  public getPolicyReferral() {
    return this.policyReferral;
  }
}
